import { useEffect, useRef } from 'react';
import useWalletContext from 'src/hooks/useWalletContext';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { isWalletBlacklistedForEmbeddedWallets } from 'src/constants/wallets';

/**
 * Disables the ENABLE_EMBEDDED_WALLET feature flag when a blacklisted wallet is connected.
 * Restores the flag to its original value when a blacklisted wallet is disconnected.
 * Ensures the flag stays disabled even if the sync process tries to enable it.
 */
export const useDisableEmbeddedWalletForBlacklistedWallets = () => {
	const walletContext = useWalletContext();
	const setFeatureFlagStore = useFeatureFlagStore((s) => s.set);
	const embeddedWalletFlagEnabled = useFeatureFlagStore(
		(s) => s.flags.ENABLE_EMBEDDED_WALLET._enabled
	);
	const originalFlagValueRef = useRef<boolean | null>(null);

	useEffect(() => {
		const walletAdapterName = walletContext.wallet?.adapter?.name;
		const isBlacklistedWallet =
			isWalletBlacklistedForEmbeddedWallets(walletAdapterName);

		if (isBlacklistedWallet) {
			// Store the original value if we haven't already
			if (originalFlagValueRef.current === null) {
				originalFlagValueRef.current = embeddedWalletFlagEnabled;
			}

			// Ensure the flag stays disabled when a blacklisted wallet is connected
			if (embeddedWalletFlagEnabled) {
				setFeatureFlagStore((stateDraft) => {
					// @ts-expect-error - _enabled is readonly in the type but mutable in immer draft
					stateDraft.flags.ENABLE_EMBEDDED_WALLET._enabled = false;
				});
			}
		} else {
			// Restore the original value if we had stored it
			if (originalFlagValueRef.current !== null) {
				setFeatureFlagStore((stateDraft) => {
					// @ts-expect-error - _enabled is readonly in the type but mutable in immer draft
					stateDraft.flags.ENABLE_EMBEDDED_WALLET._enabled =
						originalFlagValueRef.current ?? false;
				});
				originalFlagValueRef.current = null;
			}
		}
	}, [
		walletContext.wallet?.adapter?.name,
		embeddedWalletFlagEnabled,
		setFeatureFlagStore,
	]);
};
